import { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ConditionGroup, type RuleCondition, type TargetType, type BackendOperator } from "./ConditionGroup";

interface RuleGroup {
  group_join_operator: "AND" | "OR" | null;
  rules: RuleCondition[];
}

interface CriteriaState {
  rule_groups: RuleGroup[];
}

const LEGACY_OP_MAP: Record<string, BackendOperator> = {
  equals: "EQ",
  not_equals: "NEQ",
  contains: "CONTAINS",
  not_contains: "NOT_CONTAINS",
  starts_with: "STARTS_WITH",
  ends_with: "ENDS_WITH",
  greater_than: "GT",
  less_than: "LT",
  greater_than_or_equal: "GTE",
  less_than_or_equal: "LTE",
  in: "IN",
  not_in: "NOT_IN",
};

function emptyCondition(joinOp: "AND" | "OR" | null = null): RuleCondition {
  return {
    rule_join_operator: joinOp,
    target_type: "DEVICE",
    param: "",
    operator: "",
    value: "",
    data_type: "TEXT",
  };
}

function emptyGroup(joinOp: "AND" | "OR" | null = null): RuleGroup {
  return { group_join_operator: joinOp, rules: [emptyCondition(null)] };
}

/** Parse incoming criteria (rule_groups, legacy and/or, or string) into our state. */
function parseIncoming(value: any): CriteriaState | null {
  if (!value) return null;
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;

  const ruleGroups = parsed.rule_groups || parsed.tag_criteria?.rule_groups;
  if (Array.isArray(ruleGroups) && ruleGroups.length > 0) {
    return {
      rule_groups: ruleGroups.map((g: any, gi: number) => ({
        group_join_operator: gi === 0 ? null : (g.group_join_operator || "OR"),
        rules: (g.rules || []).map((r: any, ri: number) => ({
          rule_join_operator: ri === 0 ? null : (r.rule_join_operator || "AND"),
          target_type: (r.target_type || "DEVICE") as TargetType,
          param: r.param || "",
          operator: (r.operator || "") as BackendOperator | "",
          value: r.value ?? "",
          data_type: r.data_type || "TEXT",
        })),
      })),
    };
  }

  // Legacy { and|or: [...] }
  if (parsed.and || parsed.or) {
    const list = parsed.and || parsed.or;
    const join = parsed.and ? "AND" : "OR";
    return {
      rule_groups: [
        {
          group_join_operator: null,
          rules: (list as any[]).map((c, i) => ({
            rule_join_operator: i === 0 ? null : (join as "AND" | "OR"),
            target_type: "DEVICE",
            param: c.field || "",
            operator: (LEGACY_OP_MAP[c.operator] || c.operator || "EQ") as BackendOperator,
            value: c.value || "",
            data_type: "TEXT",
          })),
        },
      ],
    };
  }

  return null;
}

interface TagCriteriaBuilderProps {
  value: any;
  onChange: (criteria: any) => void;
}

export function TagCriteriaBuilder({ value, onChange }: TagCriteriaBuilderProps) {
  const [state, setState] = useState<CriteriaState>(
    () => parseIncoming(value) || { rule_groups: [emptyGroup(null)] }
  );

  // Hydrate state when the incoming value identity changes (e.g. opening edit dialog)
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      const parsed = parseIncoming(value);
      if (parsed) setState(parsed);
    }
  }, [value]);

  const emit = (next: CriteriaState) => {
    setState(next);
    // Strip incomplete rules; keep groups with at least one valid rule.
    const cleaned = {
      rule_groups: next.rule_groups
        .map((g, gi) => ({
          group_join_operator: gi === 0 ? null : (g.group_join_operator || "OR"),
          rules: g.rules
            .filter((r) => r.target_type && r.param && r.operator && r.value !== "")
            .map((r, ri) => {
              const isList = r.operator === "IN" || r.operator === "NOT_IN";
              const value: unknown = isList
                ? String(r.value)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : r.value;
              return {
                rule_join_operator: ri === 0 ? null : (r.rule_join_operator || "AND"),
                target_type: r.target_type,
                param: r.param,
                operator: r.operator,
                value,
                data_type: r.data_type || "TEXT",
              };
            }),
        }))
        .filter((g) => g.rules.length > 0),
    };
    onChange(cleaned.rule_groups.length > 0 ? cleaned : null);
  };

  const updateGroupRules = (gi: number, rules: RuleCondition[]) => {
    const next = { ...state, rule_groups: state.rule_groups.map((g, i) => (i === gi ? { ...g, rules } : g)) };
    emit(next);
  };

  const updateGroupJoin = (gi: number, op: "AND" | "OR") => {
    const next = {
      ...state,
      rule_groups: state.rule_groups.map((g, i) => (i === gi ? { ...g, group_join_operator: op } : g)),
    };
    emit(next);
  };

  const addGroup = () => {
    emit({ ...state, rule_groups: [...state.rule_groups, emptyGroup("OR")] });
  };

  const removeGroup = (gi: number) => {
    if (state.rule_groups.length === 1) return;
    const remaining = state.rule_groups.filter((_, i) => i !== gi);
    if (remaining[0]) remaining[0] = { ...remaining[0], group_join_operator: null };
    emit({ ...state, rule_groups: remaining });
  };

  const generatePreview = (): string => {
    const parts: string[] = [];
    state.rule_groups.forEach((g) => {
      const valid = g.rules.filter((r) => r.param && r.operator && r.value !== "");
      if (valid.length === 0) return;
      const inner = valid
        .map((r, i) =>
          i === 0
            ? `${r.target_type}.${r.param} ${r.operator} "${r.value}"`
            : `${r.rule_join_operator || "AND"} ${r.target_type}.${r.param} ${r.operator} "${r.value}"`
        )
        .join(" ");
      const piece = `( ${inner} )`;
      if (parts.length === 0) parts.push(piece);
      else parts.push(`${g.group_join_operator || "OR"} ${piece}`);
    });
    return parts.length ? parts.join(" ") : "No valid rules yet";
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-medium">Query Builder</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Define multiple groups of conditions. Groups are combined with the operator shown between them.
        </p>
      </div>

      <div className="space-y-3">
        {state.rule_groups.map((group, gi) => (
          <div key={gi} className="space-y-2">
            <ConditionGroup
              groupIndex={gi}
              conditions={group.rules}
              onConditionsChange={(rules) => updateGroupRules(gi, rules)}
              onRemoveGroup={() => removeGroup(gi)}
              canRemoveGroup={state.rule_groups.length > 1}
            />

            {gi < state.rule_groups.length - 1 && (
              <div className="flex items-center justify-center">
                <Select
                  value={state.rule_groups[gi + 1].group_join_operator || "OR"}
                  onValueChange={(v: "AND" | "OR") => updateGroupJoin(gi + 1, v)}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addGroup} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Create New Group
      </Button>

      <div className="p-3 bg-info/10 border border-info/20 rounded-lg">
        <p className="text-xs font-semibold text-info mb-1">RULE PREVIEW</p>
        <code className="text-sm text-info">{generatePreview()}</code>
      </div>
    </div>
  );
}
